import {pollAndThenPromise} from '../utils'
import {sendOptimizelyEvent} from '../track/helpers'

/**
 * Initializes Optimizely window object
 * @type {Array}
 */
window.optimizely = window.optimizely || []

/**
 * Fires an event to Optimizely when our SPA is initialized and ready
 * @return {void}
 */
export function pageLoaded() {
  sendOptimizelyEvent('page', {pageName: 'spaReady'})
  // Need a secondary Optimizely API Event to use only for
  // homepage targetting. 'spaReady' currently gets invoked on any page on TBT.
  sendOptimizelyEvent('page', {pageName: 'spaReadyHp'})
}

/**
 * Waits until the Optimizely snippet has been loaded so that you can call synchronous API methods
 * like optimizely.get(). The snippet is loaded asynchronously in index.html, so there is no guarantee
 * that it has finished loading otherwise.
 * @returns {Promise} Promise that resolves when the Optimizely snippet has loaded
 */
export function waitUntilLoaded() {
  return pollAndThenPromise(() => window.optimizely && window.optimizely.get, 50)
}

/**
 * Checks if the given Optimizely experiment is running, i.e. has been started and has not been paused
 * or archived. Precondition is that the Optimizely snippet has loaded.
 * @param {string} experimentId - The Optimizely experiment ID, e.g. "13111350268"
 * @returns {boolean}
 */
export function isExperimentRunning(experimentId) {
  return !!window.optimizely.get('data').experiments[experimentId]
}

/**
 * Gets the variation ID into which Optimizely has bucketed the user for the given experiment.
 * Precondition is that the Optimizely snippet has loaded.
 * @param {string} experimentId - The Optimizely experiment ID, e.g. "13111350268"
 * @returns {?string} The Optimizely variation ID, e.g. "13109540340", or undefined if the experiment
 * is not active.
 */
export function getBucketedVariation(experimentId) {
  const state = window.optimizely.get('state').getExperimentStates()[experimentId]
  return state && state.variation && state.variation.id
}

/**
 * Forces the user into the given variation for the given experiment
 * @param {string} experimentId - The Optimizely experiment ID, e.g. "13111350268"
 * @param {string} variationId - The Optimizely variation ID, e.g. "13109540340"
 * @return {void}
 */
export function setBucketedVariation(experimentId, variationId) {
  window.optimizely.push({
    type: 'bucketVisitor',
    experimentId,
    variationId,
  })
}

/**
 * Fires Optimizely page method to activate experiment for page
 * @param {string} experimentApiName - Optimizely experiment API name to activate
 * @return {void}
 */
export function activatePageExperiment(experimentApiName) {
  sendOptimizelyEvent('page', {pageName: experimentApiName})
}

/**
 * Captures custom audience attributes for Optimizely to target
 * @param {string} attributeApiName - Optimizely attribute API name
 * @param {(string|number|boolean)} attributeValue - Attribute value
 * @return {void}
 */
export const captureAudienceAttributes = (attributeApiName, attributeValue) => {
  sendOptimizelyEvent('user', {attributes: {[attributeApiName]: attributeValue}})
}

/**
 * Fires custom Optimizely tracking events to capture visitor actions
 * @param {string} eventName - Custom Optimizely event name
 * @return {void}
 */
export function fireOptimizelyEvent(eventName) {
  sendOptimizelyEvent('event', {eventName})
}

/**
 * Fires an event to Optimizely to pass revenue in cents
 * @param {integer} revenue - total revenue in cents
 * @returns {void}
 */
export const checkoutSubmitClickedOptimizely = (revenue) =>
  sendOptimizelyEvent('event', {
    eventName: 'clickedCheckoutSubmitOptimizely',
    tags: {revenue},
  })
